import React from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  CheckCircle,
  CreditCard,
  Mail,
  MessageSquare,
  Phone,
  Smartphone,
} from 'lucide-react';
import { useStats, useMessages, useGmailStatus, useUserQuery, useApps } from '../lib/hooks';
import { getAppBadge, AppBadge, AppInfo } from '../lib/appBadge';

interface NetworkInfo {
  provider: string;
  location: string;
  status: 'online' | 'offline';
}

interface Stats {
  messagesTotal: number;
  messagesThisMonth: number;
  remainingCredits: number;
  phoneVerified: boolean;
  phoneNumber: string;
  networkInfo?: NetworkInfo;
}

interface Message {
  id: string;
  direction: 'inbound' | 'outbound';
  content: string;
  response?: string;
  timestamp: string;
  tokensUsed?: number;
}

interface AppsResponse {
  serviceNumber?: string;
  apps?: AppInfo[];
}

interface GmailStatus {
  connected: boolean;
}

type DeviceType = 'inreach' | 'phone' | 'zoleo';

const DEVICE_LABELS: Record<DeviceType, string> = {
  inreach: 'Garmin InReach',
  phone: 'Phone (satellite SMS)',
  zoleo: 'Zoleo',
};

interface UserInfo {
  phoneNumber?: string;
  phoneVerified?: boolean;
  deviceType?: DeviceType;
  approvalStatus?: string;
}

const PENDING_BANNER =
  "Your account is awaiting approval — you'll get a text when you're live.";

const StatCard = ({ title, value, subtitle, icon: Icon }: any) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</p>
        <p className="mt-2 text-3xl font-semibold text-gray-900">{value}</p>
        {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
      </div>
      {Icon && (
        <div className="ml-4 p-3 bg-gray-50 rounded-lg">
          <Icon className="w-6 h-6 text-gray-600" />
        </div>
      )}
    </div>
  </div>
);

const ModernDashboard: React.FC = () => {
  const { data: stats } = useStats() as { data?: Stats };
  const { data: messagesData } = useMessages(5) as { data?: { messages: Message[] } };
  const { data: gmailStatus } = useGmailStatus() as { data?: GmailStatus };
  const { data: user } = useUserQuery() as { data?: UserInfo };
  const { data: appsData } = useApps() as { data?: AppsResponse };

  const messages = messagesData?.messages || [];
  const apps = appsData?.apps || [];
  const phoneVerified = stats?.phoneVerified ?? user?.phoneVerified ?? false;
  const phoneNumber = stats?.phoneNumber || user?.phoneNumber || '';

  return (
    <div>
      {user?.approvalStatus && user.approvalStatus !== 'approved' && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          {PENDING_BANNER}
        </div>
      )}

      {stats && !phoneVerified && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-900">Finish setting up</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Verify your phone number to start sending and receiving satellite messages.
              </p>
            </div>
            <Link
              to="/setup"
              className="ml-4 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 transition-colors whitespace-nowrap"
            >
              Complete setup
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title="Total Messages" value={stats?.messagesTotal ?? 0} icon={MessageSquare} />
        <StatCard
          title="This Month"
          value={stats?.messagesThisMonth ?? 0}
          subtitle="Messages sent"
          icon={Activity}
        />
        <StatCard
          title="Credits"
          value={stats?.remainingCredits ?? '—'}
          subtitle="Available"
          icon={CreditCard}
        />
        <StatCard
          title="Phone Status"
          value={phoneVerified ? 'Verified' : 'Not Verified'}
          icon={Phone}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Messages</h3>
            </div>

            {messages.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {messages.map(message => {
                  const badge = getAppBadge(message.content, apps);
                  return (
                    <div key={message.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center flex-wrap">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            message.direction === 'inbound' ? 'bg-green-500' : 'bg-blue-500'
                          } mr-2`}
                        />
                        <span className="text-sm font-medium text-gray-900">
                          {message.direction === 'inbound' ? 'Received' : 'Sent'}
                        </span>
                        {badge && <AppBadge label={badge} />}
                        <span className="ml-2 text-xs text-gray-500">
                          {new Date(message.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">{message.content}</p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-6 py-12 text-center">
                <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-sm font-medium text-gray-700">Ready for Transmission</p>
                <p className="text-xs text-gray-500 mt-1">Send your first SMS to establish connection</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Gmail</h3>
            {gmailStatus?.connected ? (
              <div className="flex items-center text-green-600 text-sm font-medium">
                <CheckCircle className="w-4 h-4 mr-2" />
                Connected
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-3">
                  Connect Gmail to enable email commands over SMS.
                </p>
                <Link
                  to="/settings"
                  className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Connect Gmail
                </Link>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Device</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Device</span>
                {user?.deviceType ? (
                  <span className="text-sm font-medium text-gray-900 flex items-center">
                    <Smartphone className="w-4 h-4 mr-1 text-gray-400" />
                    {DEVICE_LABELS[user.deviceType]}
                  </span>
                ) : (
                  <Link
                    to="/setup"
                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Not set — choose in Setup
                  </Link>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Paired number</span>
                <span className="text-sm font-medium text-gray-900">
                  {phoneNumber || 'Not verified'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Status</span>
                <span className="text-sm font-medium text-gray-900">
                  {stats?.networkInfo?.status === 'online' ? 'Online' : 'Awaiting activity'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernDashboard;
